import { FirebaseError, initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  signInWithCustomToken,
  signOut as _signOut,
} from 'firebase/auth';
import { config } from '../config';

export const app = initializeApp({
  projectId: config.firebase.projectId,
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
  storageBucket: config.firebase.storageBucket,
  ...(config.firebase.appId && { appId: config.firebase.appId }),
});

export const auth = getAuth(app);

if (config.useEmulators) {
  connectAuthEmulator(auth, config.authEmulatorUrl, { disableWarnings: true });
}

export interface SendOtpResult {
  message: string;
  expiresIn: number;
}

export interface VerifyOtpResult {
  isNewUser: boolean;
}

export async function sendPhoneOTP(phoneNumber: string): Promise<SendOtpResult> {
  const res = await fetch(`${config.apiUrl}/api/v1/auth/phone/send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: phoneNumber }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 429) {
      throw new Error('Too many attempts. Please try again later.');
    }
    if (res.status === 422) {
      throw new Error('Please enter a valid phone number (e.g. +919072278060).');
    }
    throw new Error((body as { detail?: string })?.detail ?? 'Failed to send verification code.');
  }

  return res.json() as Promise<SendOtpResult>;
}

export async function verifyPhoneOTP(phoneNumber: string, code: string): Promise<VerifyOtpResult> {
  const res = await fetch(`${config.apiUrl}/api/v1/auth/phone/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: phoneNumber, code }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const detail = (body as { detail?: string })?.detail;
    if (res.status === 400) {
      throw new Error(detail ?? 'Invalid or expired verification code.');
    }
    throw new Error(detail ?? 'Verification failed. Please try again.');
  }

  const data = (await res.json()) as { customToken: string; isNewUser: boolean };

  // Sign in to Firebase with the custom token from the backend
  try {
    await signInWithCustomToken(auth, data.customToken);
  } catch (err) {
    if (err instanceof FirebaseError) {
      throw new Error('Authentication failed. Please try again.');
    }
    throw err;
  }

  return { isNewUser: data.isNewUser };
}

export async function signOutUser() {
  return _signOut(auth);
}
